import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap, map, catchError, of, throwError } from 'rxjs';
import {
  Ticket,
  TicketStatus,
  TicketPriority,
  TicketFilterState,
  ViewMode,
  Comment,
  Activity,
  UserRef,
} from '../models/ticket.model';
import { DepartmentService } from './department.service';
import { ProjectService } from './project.service';
import { ToastService } from './toast.service';
import { AuthenticationService } from './Authentication/authentication.service';
import { PermissionService } from './Authentication/permission.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class TicketService {
  private readonly API_URL = `${environment.apiUrl}/tickets`;
  private readonly http = inject(HttpClient);
  private readonly departmentService = inject(DepartmentService);
  private readonly projectService = inject(ProjectService);
  private readonly toastService = inject(ToastService);

  private readonly FILTER_STORAGE_KEY = 'savinda_tms_ticket_filters';
  private readonly WORKSPACE_ROUTE_KEY = 'savinda_tms_last_workspace_route';

  readonly viewMode = signal<ViewMode>(
    (localStorage.getItem('savinda_tms_view_mode') as ViewMode) || 'kanban'
  );

  readonly lastWorkspaceRoute = signal<string>(
    sessionStorage.getItem('savinda_tms_last_workspace_route') || '/tickets'
  );

  readonly userFilterPreferences = signal<Partial<TicketFilterState>>({
    search: '',
    departmentId: 'all',
    projectId: 'all',
    priority: 'all',
    status: 'all',
    category: 'all',
    assigneeId: 'all',
    creatorId: 'all',
  });

  readonly filterState = signal<TicketFilterState>({
    search: '',
    departmentId: 'all',
    projectId: 'all',
    priority: 'all',
    status: 'all',
    category: 'all',
    assigneeId: 'all',
    creatorId: 'all',
    myTicketsOnly: false,
    assignedToMeOnly: false,
    sortBy: 'updatedAt',
    sortOrder: 'desc',
  });

  readonly filter = this.filterState;

  readonly tickets = signal<Ticket[]>([]);
  readonly comments = signal<Record<string, Comment[]>>({});
  readonly activities = signal<Record<string, Activity[]>>({});
  readonly selectedTicket = signal<Ticket | null>(null);
  readonly refreshSignal = signal<number>(0);

  /** Global create ticket modal state & drafted template */
  readonly isCreateModalOpen = signal<boolean>(false);
  readonly createModalInitialData = signal<{
    title?: string;
    description?: string;
    departmentId?: string;
    projectId?: string;
    priority?: TicketPriority;
    assigneeId?: string;
  } | null>(null);

  openCreateModal(initialData?: {
    title?: string;
    description?: string;
    departmentId?: string;
    projectId?: string;
    priority?: TicketPriority;
    assigneeId?: string;
  }): void {
    this.createModalInitialData.set(initialData || null);
    this.isCreateModalOpen.set(true);
  }

  openGuestAccountRequestTicket(): void {
    const draftDescription = `<p><strong>Dedicated User Account Request</strong></p><p>Please provision a dedicated Support desk user account with the following details:</p><ul><li>Name: [ Enter Your Name ]</li><li>Email: [ Enter your company email address ]</li><li>Employee Number: [ Enter your employee number, e.g. EMP00028 ]</li><li>Portal User: [ Enter Portal User username ]</li><li>Department: [ Enter your department, e.g. IT, HR, Maintenance ]</li><li>Justification / Notes: [ Enter your job title or reason for access ]</li></ul><p><em>(Please replace all bracketed items with your details before clicking Submit.)</em></p>`;

    this.openCreateModal({
      title: 'User Account Request for Suport Desk - [Your Name / Employee No]',
      description: draftDescription,
      departmentId: '4',
      assigneeId: '4',
      priority: 'High',
    });
  }

  closeCreateModal(): void {
    this.isCreateModalOpen.set(false);
    this.createModalInitialData.set(null);
  }

  private readonly authService = inject(AuthenticationService);
  private readonly permissionService = inject(PermissionService);

  /** True when the current user is EMPLOYEE or DEPARTMENT_HEAD — dept filter is locked to their department */
  readonly isDeptLocked = computed(() => this.permissionService.isDeptScopedRole());

  constructor() {
    if (this.authService.getAccessToken()) {
      this.restoreSavedUserFilters();
      // Auto-apply department scope for non-admin roles
      const user = this.authService.currentUser();
      const roles: string[] = (user as any)?.roles || [];
      const isAdmin = roles.includes('ADMIN') || roles.includes('SYSTEM_ADMIN');
      const deptId = user?.departmentId;
      if (!isAdmin && deptId) {
        this.filterState.update((prev) => ({ ...prev, departmentId: String(deptId) }));
      }
    }
  }

  private restoreSavedUserFilters(): void {
    const user = this.authService.currentUser();
    const userId = user ? String(user.id) : '';
    try {
      const raw = sessionStorage.getItem(this.FILTER_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && (!parsed.userId || parsed.userId === userId) && parsed.filters) {
        this.userFilterPreferences.update((prev) => ({ ...prev, ...parsed.filters }));
        this.filterState.update((prev) => ({ ...prev, ...parsed.filters }));
      }
    } catch {
      // Ignore invalid JSON in sessionStorage
    }
  }

  setLastWorkspaceRoute(route: string): void {
    this.lastWorkspaceRoute.set(route);
    sessionStorage.setItem(this.WORKSPACE_ROUTE_KEY, route);
  }

  saveUserFilterPreferences(updates: Partial<TicketFilterState>): void {
    const user = this.authService.currentUser();
    const userId = user ? String(user.id) : '';
    this.userFilterPreferences.update((prev) => {
      const next = { ...prev, ...updates };
      try {
        sessionStorage.setItem(this.FILTER_STORAGE_KEY, JSON.stringify({ userId, filters: next }));
      } catch {
        // Ignore storage errors
      }
      return next;
    });
  }

  loadTickets(): Observable<Ticket[]> {
    if (!this.authService.getAccessToken()) {
      return of([]);
    }

    const filter = this.filterState();
    const queryParts: string[] = ['limit=500'];

    if (filter.search?.trim()) queryParts.push(`search=${encodeURIComponent(filter.search.trim())}`);
    if (filter.departmentId !== 'all' && filter.departmentId !== undefined && filter.departmentId !== '') queryParts.push(`departmentId=${filter.departmentId}`);
    if (filter.projectId !== 'all' && filter.projectId !== undefined && filter.projectId !== '') queryParts.push(`projectId=${filter.projectId}`);
    if (filter.priority !== 'all' && filter.priority !== 'All') queryParts.push(`priority=${this.toBackendPriority(filter.priority as TicketPriority)}`);
    if (filter.status !== 'all' && filter.status !== 'All') queryParts.push(`status=${this.toBackendStatus(filter.status as TicketStatus)}`);
    if (filter.category !== 'all' && filter.category !== 'All') queryParts.push(`category=${encodeURIComponent(String(filter.category))}`);
    if (filter.assigneeId !== 'all' && filter.assigneeId !== undefined && filter.assigneeId !== '') queryParts.push(`assigneeId=${filter.assigneeId}`);
    if (filter.creatorId !== 'all' && filter.creatorId !== undefined && filter.creatorId !== '') queryParts.push(`createdById=${filter.creatorId}`);
    if (filter.myTicketsOnly) queryParts.push(`myTickets=true`);
    if (filter.assignedToMeOnly) queryParts.push(`assignedToMe=true`);
    if (filter.excludeMyCreated) queryParts.push(`excludeMyCreated=true`);

    const url = `${this.API_URL}?${queryParts.join('&')}`;

    return this.http.get<any>(url).pipe(
      map((res) => {
        let rawItems: any[] = [];
        if (Array.isArray(res)) {
          rawItems = res;
        } else if (res?.items && Array.isArray(res.items)) {
          rawItems = res.items;
        } else if (res?.data?.items && Array.isArray(res.data.items)) {
          rawItems = res.data.items;
        } else if (res?.data && Array.isArray(res.data)) {
          rawItems = res.data;
        } else if (res?.data?.data && Array.isArray(res.data.data)) {
          rawItems = res.data.data;
        }

        return rawItems.map((item: any) => this.mapBackendTicket(item));
      }),
      tap((items: Ticket[]) => this.tickets.set(items)),
      catchError(() => of([])),
    );
  }

  // Filtered tickets signal
  readonly filteredTickets = computed(() => {
    const filter = this.filterState();
    const currentUser = this.authService.currentUser();
    const currentUserId = currentUser ? String(currentUser.id) : null;

    return this.tickets().filter((t) => {
      if (filter.search?.trim()) {
        const query = filter.search.toLowerCase();
        const matches =
          (t.ticketNumber && t.ticketNumber.toLowerCase().includes(query)) ||
          (t.title && t.title.toLowerCase().includes(query)) ||
          (t.description && t.description.toLowerCase().includes(query)) ||
          (t.departmentName && t.departmentName.toLowerCase().includes(query));
        if (!matches) return false;
      }

      if (filter.departmentId !== 'all' && filter.departmentId !== undefined && filter.departmentId !== '') {
        if (String(t.departmentId) !== String(filter.departmentId)) return false;
      }

      if (filter.projectId !== 'all' && filter.projectId !== undefined && filter.projectId !== '') {
        if (String(t.projectId) !== String(filter.projectId)) return false;
      }

      if (filter.status !== 'all' && filter.status !== 'All') {
        if (t.status !== filter.status) return false;
      }

      if (filter.priority !== 'all' && filter.priority !== 'All') {
        if (t.priority !== filter.priority) return false;
      }

      if (filter.category !== 'all' && filter.category !== 'All') {
        if (t.category !== filter.category) return false;
      }

      if (filter.assignedToMeOnly) {
        if (!currentUserId) return false;
        const assigneeId = t.assignee ? String(t.assignee.id) : '';
        if (assigneeId !== currentUserId) return false;
      }

      if (filter.myTicketsOnly) {
        if (!currentUserId) return false;
        const creatorId = t.creator ? String(t.creator.id) : '';
        if (creatorId !== currentUserId) return false;
      }

      if (filter.excludeMyCreated && currentUserId) {
        const creatorId = t.creator ? String(t.creator.id) : '';
        if (creatorId === currentUserId) return false;
      }

      if (filter.creatorId !== 'all' && filter.creatorId !== undefined && filter.creatorId !== '') {
        const creatorId = t.creator ? String(t.creator.id) : '';
        if (creatorId !== String(filter.creatorId)) return false;
      }

      if (filter.assigneeId !== 'all' && filter.assigneeId !== undefined && filter.assigneeId !== '') {
        if (filter.assigneeId === 'unassigned') {
          if (t.assignee) return false;
        } else {
          const assigneeId = t.assignee ? String(t.assignee.id) : '';
          if (assigneeId !== String(filter.assigneeId)) return false;
        }
      }

      return true;
    });
  });

  // Kanban Swimlanes
  readonly kanbanColumns = computed(() => {
    const list = this.filteredTickets();
    const statuses: TicketStatus[] = ['Open', 'In Progress', 'On Hold', 'Resolved', 'Closed', 'Cancelled'];

    return statuses.map((status) => ({
      status,
      count: list.filter((t) => t.status === status).length,
      tickets: list.filter((t) => t.status === status),
    }));
  });

  // Auto Assignment Logic: Project Default -> Department Default -> No Assignee
  calculateAutoAssignee(departmentId: number | string, projectId?: number | string): { assignee?: UserRef; autoAssigned: boolean } {
    if (projectId) {
      const proj = this.projectService.projects().find((p) => String(p.id) === String(projectId));
      if (proj?.defaultAssignee) {
        return { assignee: proj.defaultAssignee, autoAssigned: true };
      }
    }

    const dept = this.departmentService.departments().find((d) => String(d.id) === String(departmentId));
    if (dept?.defaultAssignee) {
      return { assignee: dept.defaultAssignee, autoAssigned: true };
    }

    return { assignee: undefined, autoAssigned: false };
  }

  // Dashboard Executive Metrics
  readonly metrics = computed(() => {
    const all = this.tickets();
    return {
      total: all.length,
      open: all.filter((t) => t.status === 'Open').length,
      inProgress: all.filter((t) => t.status === 'In Progress').length,
      critical: all.filter((t) => t.priority === 'Critical').length,
    };
  });

  // Dashboard Statistics
  readonly dashboardStats = computed(() => {
    const all = this.tickets();
    const todayStr = new Date().toISOString().split('T')[0];

    const openCount = all.filter((t) => t.status === 'Open' || t.status === 'In Progress' || t.status === 'On Hold').length;
    const overdueCount = all.filter((t) => t.dueDate && t.dueDate < todayStr && t.status !== 'Resolved' && t.status !== 'Closed' && t.status !== 'Cancelled').length;
    const resolvedTodayCount = all.filter((t) => (t.status === 'Resolved' || t.status === 'Closed') && t.updatedAt.startsWith(todayStr)).length;

    return {
      openCount,
      assignedToMeCount: openCount,
      overdueCount,
      resolvedTodayCount,
      totalCount: all.length,
    };
  });

  setViewMode(mode: ViewMode): void {
    this.viewMode.set(mode);
    localStorage.setItem('savinda_tms_view_mode', mode);
  }

  updateFilter(updates: Partial<TicketFilterState>): void {
    this.filterState.update((prev) => ({ ...prev, ...updates }));
    this.loadTickets().subscribe();
  }

  resetFilters(): void {
    const currentAssignedToMe = this.filterState().assignedToMeOnly;
    const currentMyTickets = this.filterState().myTicketsOnly;
    const currentExcludeMyCreated = this.filterState().excludeMyCreated;

    // Preserve the dept lock for non-admin roles unless in Created By Me view
    const user = this.authService.currentUser();
    const currentUserId = user ? String(user.id) : '';
    const roles: string[] = (user as any)?.roles || [];
    const isAdmin = roles.includes('ADMIN') || roles.includes('SYSTEM_ADMIN');
    const lockedDeptId = (!isAdmin && user?.departmentId && !currentMyTickets) ? String(user.departmentId) : 'all';

    this.userFilterPreferences.set({
      search: '',
      departmentId: 'all',
      projectId: 'all',
      priority: 'all',
      status: 'all',
      category: 'all',
      assigneeId: 'all',
      creatorId: 'all',
    });
    sessionStorage.removeItem(this.FILTER_STORAGE_KEY);

    this.filterState.set({
      search: '',
      departmentId: lockedDeptId,
      projectId: 'all',
      priority: 'all',
      status: 'all',
      category: 'all',
      assigneeId: currentAssignedToMe ? (currentUserId || 'all') : 'all',
      creatorId: currentMyTickets ? (currentUserId || 'all') : 'all',
      myTicketsOnly: !!currentMyTickets,
      assignedToMeOnly: !!currentAssignedToMe,
      excludeMyCreated: !!currentExcludeMyCreated,
      sortBy: 'updatedAt',
      sortOrder: 'desc',
    });
    this.loadTickets().subscribe();
  }

  getTicketById(ticketId: number | string): Ticket | undefined {
    const idStr = String(ticketId);
    return this.tickets().find((ticket) => String(ticket.id) === idStr);
  }

  resetFilter(): void {
    this.resetFilters();
  }

  createTicket(data: {
    title: string;
    description: string;
    departmentId: string | number;
    projectId?: string | number;
    priority: TicketPriority;
    assigneeId?: string | number;
    dueDate?: string;
  }): Observable<Ticket> {
    const payload = {
      title: data.title,
      description: data.description,
      departmentId: Number(data.departmentId),
      projectId: data.projectId ? Number(data.projectId) : undefined,
      priority: this.toBackendPriority(data.priority),
      assigneeId: data.assigneeId ? Number(data.assigneeId) : undefined,
      dueDate: data.dueDate,
    };

    return this.http.post<any>(this.API_URL, payload).pipe(
      map((res) => this.mapBackendTicket(res.data ? res.data : res)),
      tap((newTicket) => {
        this.tickets.update((items) => [newTicket, ...items]);
        this.toastService.success(`Ticket ${newTicket.ticketNumber} Created`, 'Created successfully');
      }),
    );
  }

  getTicketWorkspace(ticketId: number | string): Observable<any> {
    return this.http.get<any>(`${this.API_URL}/${ticketId}/workspace`).pipe(
      map((res) => {
        const data = res.data ? res.data : res;
        if (data && data.attachments && Array.isArray(data.attachments)) {
          if (data.ticket) {
            data.ticket.attachments = data.attachments.filter((a: any) => a.entityType === 'ticket' && String(a.entityId) === String(ticketId));
          }
          if (data.comments && Array.isArray(data.comments)) {
            data.comments.forEach((c: any) => {
              c.attachments = data.attachments.filter((a: any) => a.entityType === 'comment' && String(a.entityId) === String(c.id));
            });
          }
        }
        return data;
      }),
    );
  }

  getAttachmentDownloadUrl(attachmentId: number | string): string {
    const token = this.authService.getAccessToken() || '';
    return `${environment.apiUrl}/attachments/${attachmentId}/download?access_token=${encodeURIComponent(token)}`;
  }

  // Optimistic Status Update with Automatic Rollback
  updateTicketStatus(ticketId: number | string, newStatus: TicketStatus, _currentUser?: string): Observable<any> {
    const idStr = String(ticketId);
    const ticketIndex = this.tickets().findIndex((t) => String(t.id) === idStr);
    const originalTicket = ticketIndex !== -1 ? this.tickets()[ticketIndex] : null;

    if (originalTicket) {
      const updatedTicket = { ...originalTicket, status: newStatus, updatedAt: new Date().toISOString() };
      this.tickets.update((items) => items.map((t) => (String(t.id) === idStr ? updatedTicket : t)));
      if (String(this.selectedTicket()?.id) === idStr) {
        this.selectedTicket.set(updatedTicket);
      }
    }

    return this.http.patch<any>(`${this.API_URL}/${ticketId}/status`, { status: this.toBackendStatus(newStatus) }).pipe(
      map((res) => (res.data ? res.data : res)),
      tap(() => {
        this.triggerWorkspaceRefresh(ticketId);
      }),
      catchError((err) => {
        if (originalTicket) {
          this.tickets.update((items) => items.map((t) => (String(t.id) === idStr ? originalTicket : t)));
        }
        return throwError(() => err);
      }),
    );
  }

  updateTicketDetails(ticketId: number | string, data: { title?: string; description?: string; departmentId?: number | string; projectId?: number | string; assigneeId?: number | string; priority?: string }): Observable<any> {
    return this.http.put<any>(`${this.API_URL}/${ticketId}`, data).pipe(
      map((res) => (res.data ? res.data : res)),
      tap(() => {
        this.triggerWorkspaceRefresh(ticketId);
        this.loadTickets().subscribe();
      })
    );
  }

  assignTicket(ticketId: number | string, assignee: UserRef): void {
    this.http.put<any>(`${this.API_URL}/${ticketId}`, { assigneeId: assignee.id }).subscribe({
      next: () => {
        this.loadTickets().subscribe();
        this.toastService.success('Assignee Updated', `Assigned to ${assignee.name}`);
      },
    });
  }

  addComment(ticketId: number | string, authorOrContent: string, content?: string, _isInternalNote?: boolean): Observable<any> {
    const commentText = typeof content === 'string' ? content : authorOrContent;
    const author = typeof content === 'string' ? authorOrContent : undefined;

    if (!commentText.trim()) return of(null);
    const idNum = Number(ticketId);
    const payload: Record<string, unknown> = { ticketId: idNum, content: commentText };
    if (author) payload['author'] = author;
    if (typeof _isInternalNote === 'boolean') payload['isInternal'] = _isInternalNote;

    return this.http.post<any>(`${environment.apiUrl}/tickets/${ticketId}/comments`, payload).pipe(
      map((res) => (res.data ? res.data : res)),
      tap(() => {
        this.triggerWorkspaceRefresh(ticketId);
      }),
    );
  }

  triggerWorkspaceRefresh(ticketId?: number | string): void {
    this.refreshSignal.update((v) => v + 1);
    if (ticketId) {
      const idStr = String(ticketId);
      this.loadTicketComments(idStr);
      this.loadTicketActivities(idStr);
    }
  }

  loadTicketComments(ticketId: string): void {
    this.http.get<any>(`${environment.apiUrl}/tickets/${ticketId}/comments`).subscribe({
      next: (res) => {
        const rawComments = res.data ? res.data : (Array.isArray(res) ? res : []);
        const mapped = rawComments.map((c: any) => ({
          id: c.id,
          ticketId: c.ticketId,
          author: {
            id: c.author?.id || c.authorId,
            name: c.author?.name || 'User',
            username: c.author?.username || 'user',
            avatarUrl: c.author?.avatarUrl,
          },
          content: c.content,
          createdAt: c.createdAt,
        }));
        this.comments.update((map) => ({ ...map, [ticketId]: mapped }));
      },
    });
  }

  loadTicketActivities(ticketId: string): void {
    this.http.get<any>(`${this.API_URL}/${ticketId}/activity`).subscribe({
      next: (res) => {
        const rawActs = res.data ? res.data : (Array.isArray(res) ? res : []);
        const mapped = rawActs.map((a: any) => ({
          id: a.id,
          ticketId: a.ticketId,
          user: {
            id: a.user?.id || a.userId,
            name: a.user?.name || 'User',
            username: a.user?.username || 'user',
          },
          type: a.action ? a.action.toLowerCase() : 'updated',
          description: a.description,
          timestamp: a.createdAt,
        }));
        this.activities.update((map) => ({ ...map, [ticketId]: mapped }));
      },
    });
  }

  selectTicket(ticket: Ticket | null): void {
    this.selectedTicket.set(ticket);
    if (ticket) {
      this.loadTicketComments(ticket.id);
      this.loadTicketActivities(ticket.id);
    }
  }

  private mapBackendTicket(item: any): Ticket {
    return {
      id: String(item.id),
      ticketNumber: item.ticketNumber || `TCK-${item.id}`,
      title: item.title,
      description: item.description,
      status: this.normalizeStatus(item.status),
      priority: this.normalizePriority(item.priority),
      category: item.category || 'General',
      departmentId: item.departmentId || item.department?.id,
      departmentName: item.department?.name || 'General',
      projectId: item.projectId || item.project?.id,
      projectName: item.project?.name,
      creator: {
        id: item.createdBy?.id || item.createdById || '1',
        name: item.createdBy?.name || 'Admin',
        username: item.createdBy?.username || 'admin',
        avatarUrl: item.createdBy?.avatarUrl,
      },
      requester: item.createdBy?.name || item.createdBy?.username || 'Admin',
      requesterEmail: item.createdBy?.email || item.requesterEmail || '',
      assignee: item.assignee
        ? {
          id: item.assignee.id,
          name: item.assignee.name,
          username: item.assignee.username,
          avatarUrl: item.assignee.avatarUrl,
        }
        : undefined,
      dueDate: item.dueDate ? item.dueDate.substring(0, 10) : undefined,
      slaPolicyId: item.slaPolicyId || item.slaPolicy?.id,
      slaPolicyName: item.slaPolicy?.name,
      slaPolicySource: item.slaPolicySource,
      firstResponseDueAt: item.firstResponseDueAt,
      firstRespondedAt: item.firstRespondedAt,
      resolutionDueAt: item.resolutionDueAt,
      slaResponseStatus: item.slaResponseStatus,
      slaResolutionStatus: item.slaResolutionStatus,
      slaStatus: item.slaStatus,
      resolvedAt: item.resolvedAt,
      closedAt: item.closedAt,
      createdAt: item.createdAt || new Date().toISOString(),
      updatedAt: item.updatedAt || new Date().toISOString(),
      commentCount: item.commentCount || 0,
      attachmentCount: item.attachmentCount || 0,
      attachments: item.attachments || [],
      tags: item.tags || [],
      comments: item.comments || [],
      auditLogs: item.auditLogs || [],
      unread: false,
    };
  }

  private normalizeStatus(status: string): TicketStatus {
    if (!status) return 'Open';
    const s = String(status).toUpperCase();
    if (s === 'OPEN') return 'Open';
    if (s === 'IN_PROGRESS' || s === 'IN PROGRESS') return 'In Progress';
    if (s === 'ON_HOLD' || s === 'ON HOLD') return 'On Hold';
    if (s === 'RESOLVED') return 'Resolved';
    if (s === 'CLOSED') return 'Closed';
    if (s === 'CANCELLED') return 'Cancelled';
    return 'Open';
  }

  private normalizePriority(priority: string): TicketPriority {
    if (!priority) return 'Medium';
    const p = String(priority).toUpperCase();
    if (p === 'LOW') return 'Low';
    if (p === 'MEDIUM') return 'Medium';
    if (p === 'HIGH') return 'High';
    if (p === 'CRITICAL') return 'Critical';
    return 'Medium';
  }

  private toBackendStatus(status: TicketStatus): string {
    switch (status) {
      case 'Open': return 'OPEN';
      case 'In Progress': return 'IN_PROGRESS';
      case 'On Hold': return 'ON_HOLD';
      case 'Resolved': return 'RESOLVED';
      case 'Closed': return 'CLOSED';
      case 'Cancelled': return 'CANCELLED';
      default: return 'OPEN';
    }
  }

  private toBackendPriority(priority: TicketPriority): string {
    return (priority || 'Medium').toUpperCase();
  }
}
